// System Design case studies, part 3: payments, ticket booking, web crawler,
// distributed key-value store and live leaderboard.

export const systemDesignCaseStudies3 = [
  {
    slug: 'design-payment-system',
    title: 'Case Study: Design a Payment System',
    summary: 'Charging exactly once, a double-entry ledger, handling uncertain outcomes, webhooks and reconciliation.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design the payment flow for an online store.” Here correctness matters far more than speed: never charge twice, never lose a payment, and always be able to explain where every rupee went.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Customers pay for an order through an external payment provider
  2. Refunds, full or partial
  3. Merchants and finance can see accurate payment history

Non-functional
  - Exactly-once charging from the customer's point of view
  - Every money movement is recorded and auditable
  - Card data never touches our servers` },

      { type: 'h2', text: '2. Payment states' },
      { type: 'code', language: 'text', code: `created ──► pending (sent to provider) ──► succeeded ──► (partially_)refunded
                         │
                         ├──► failed
                         └──► unknown (timeout) ──► resolved by status check or webhook` },
      { type: 'p', text: 'Modelling the payment as an explicit state machine makes every transition deliberate and lets recovery jobs find payments stuck in intermediate states.' },

      { type: 'h2', text: '3. Deep dive: never charging twice' },
      { type: 'ul', items: [
        'Clients send an **idempotency key** with every payment request. The server stores the key with the result, so a retry returns the original outcome instead of charging again.',
        'The server passes its own idempotency key to the payment provider, so retries towards the provider are safe too.',
        'A unique constraint on (order, active payment) stops two parallel payment attempts for the same order.'
      ] },
      {"type": "diagram", "title": "Payments: components", "caption": "The payment service drives a state machine and records every money movement in the ledger. Provider results arrive by webhook, and a daily reconciliation job compares the ledger with settlement reports.", "cols": 4, "rows": 3, "nodes": [{"id": "checkout", "label": "Checkout", "kind": "client", "col": 0, "row": 1}, {"id": "pay", "label": "Payment service", "kind": "service", "col": 1, "row": 1, "sub": "state machine"}, {"id": "db", "label": "Payments & ledger", "kind": "db", "col": 2, "row": 0}, {"id": "recon", "label": "Reconciliation", "kind": "worker", "col": 3, "row": 0, "sub": "daily"}, {"id": "psp", "label": "Payment provider", "kind": "external", "col": 3, "row": 1}, {"id": "hook", "label": "Webhook handler", "kind": "service", "col": 2, "row": 2, "sub": "verify signature"}], "edges": [{"from": "checkout", "to": "pay", "label": "pay + idempotency key"}, {"from": "pay", "to": "db", "label": "record state"}, {"from": "pay", "to": "psp", "label": "charge (same key)"}, {"from": "psp", "to": "hook", "label": "result", "dashed": true}, {"from": "hook", "to": "db", "label": "update + ledger"}, {"from": "recon", "to": "db", "label": "compare"}, {"from": "recon", "to": "psp", "label": "settlement report", "dashed": true}]},
      { type: 'callout', variant: 'warning', title: 'Timeouts are not failures', text: 'If the call to the provider times out, the charge may or may not have happened. Mark the payment as unknown, then check its status with the provider (or wait for the webhook) before retrying. Retrying blindly is how customers get charged twice.' },

      { type: 'h2', text: '4. Deep dive: the ledger' },
      { type: 'p', text: 'Record money movements in a double-entry ledger: every transaction has entries that debit some accounts and credit others by exactly the same total, and entries are never edited, only added.' },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.List;

public class Ledger {
    record Entry(String txn, String account, long debit, long credit) {}

    static final List<Entry> ENTRIES = List.of(
        new Entry("order-1023", "customer_payments", 100_000, 0),
        new Entry("order-1023", "merchant_payable", 0, 97_000),
        new Entry("order-1023", "platform_fees", 0, 3_000));

    static boolean balanced(String txn) {
        long debits = ENTRIES.stream().filter(e -> e.txn().equals(txn)).mapToLong(Entry::debit).sum();
        long credits = ENTRIES.stream().filter(e -> e.txn().equals(txn)).mapToLong(Entry::credit).sum();
        return debits == credits;
    }

    public static void main(String[] args) {
        System.out.println(balanced("order-1023"));
    }
}`, output: `true` },
        { language: 'python', code: `entries = [
    {"txn": "order-1023", "account": "customer_payments", "debit": 100_000, "credit": 0},
    {"txn": "order-1023", "account": "merchant_payable", "debit": 0, "credit": 97_000},
    {"txn": "order-1023", "account": "platform_fees", "debit": 0, "credit": 3_000},
]

def balanced(txn):
    rows = [e for e in entries if e["txn"] == txn]
    return sum(e["debit"] for e in rows) == sum(e["credit"] for e in rows)

print(balanced("order-1023"))`, output: `True` },
        { language: 'javascript', code: `const entries = [
  { txn: "order-1023", account: "customer_payments", debit: 100000, credit: 0 },
  { txn: "order-1023", account: "merchant_payable",  debit: 0, credit: 97000 },
  { txn: "order-1023", account: "platform_fees",     debit: 0, credit: 3000 }
];

const balanced = (txn) => {
  const rows = entries.filter((e) => e.txn === txn);
  const debits = rows.reduce((s, e) => s + e.debit, 0);
  const credits = rows.reduce((s, e) => s + e.credit, 0);
  return debits === credits;
};

console.log(balanced("order-1023"));`, output: `true` }
      ] },
      { type: 'ul', items: [
        'Amounts are integers in the smallest unit (paise), never floating-point numbers.',
        'Corrections are new reversing entries, so history is preserved.',
        'Balances are derived from entries, which makes them auditable.'
      ] },

      { type: 'h2', text: '5. Deep dive: webhooks and reconciliation' },
      { type: 'ul', items: [
        'Providers notify you of payment results through webhooks. Verify each webhook’s signature, process it idempotently by event ID, and expect events to arrive late, twice or out of order.',
        'A daily reconciliation job compares your ledger with the provider’s settlement reports. Any mismatch creates an alert and a task for review.',
        'Recovery jobs regularly check payments stuck in pending or unknown states.'
      ] },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Using a payment provider, storing payment records and statuses.'],
        ['Mid', 'Idempotency keys, webhooks, refunds, handling failures.'],
        ['Senior', 'Uncertain outcomes after timeouts, a double-entry ledger, reconciliation, audit and compliance concerns.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Retrying a timed-out charge without an idempotency key.',
        'Storing money as floating-point numbers.',
        'Updating balances in place with no history.',
        'Trusting webhooks without verifying signatures.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you keep the order and the payment consistent without a distributed transaction?', answer: ['Use the state machine plus the outbox pattern: record the intent locally, call the provider with an idempotency key, record the outcome, and let recovery jobs resolve anything left in an intermediate state by checking with the provider.'] },
      { type: 'qa', question: 'How would you handle a partial refund?', answer: ['Create a refund record linked to the payment, call the provider’s refund API with an idempotency key, and post reversing ledger entries for the refunded amount. Never let total refunds exceed the captured amount.'] },

      { type: 'exercise', title: 'Trace a failure', task: 'The provider charged the customer, but your server crashed before saving “succeeded”. Walk through how the system recovers.', solution: `1. The payment row is still "pending" with the provider reference
2. The provider's webhook arrives (or a recovery job polls status)
   and reports success
3. The handler, idempotent by event id, moves the payment to
   "succeeded", posts ledger entries and publishes order.paid
4. Daily reconciliation would also flag it if both webhook and
   polling had failed`, language: 'text' },

      { type: 'summary', items: [
        'Model payments as a state machine, including an “unknown” state.',
        'Idempotency keys on both sides prevent double charges.',
        'A double-entry, append-only ledger makes money auditable.',
        'Verify webhooks, recover stuck payments, reconcile daily.'
      ] }
    ]
  },
  {
    slug: 'design-ticket-booking',
    title: 'Case Study: Design a Ticket Booking System',
    summary: 'Selling limited seats to huge crowds: holds, contention, virtual waiting rooms and fairness.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a ticket booking site for concerts and events.” Most of the time it is a quiet catalogue. When a popular event goes on sale, hundreds of thousands of people try to buy the same few thousand seats at the same moment. That contention is the whole problem.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Browse and search events; view the seat map
  2. Select seats and hold them while paying
  3. Complete the booking; seats are never sold twice

Non-functional
  - Strong consistency for seat ownership
  - Survive sudden spikes when popular events open
  - Browsing can be eventually consistent and heavily cached` },

      { type: 'h2', text: '2. Deep dive: holding seats' },
      { type: 'p', text: 'Paying takes a few minutes, so seats are held temporarily. A hold must be atomic and must expire if the buyer walks away.' },
      { type: 'code', language: 'sql', code: `-- Hold a seat only if it is free (or its previous hold expired)
UPDATE seats
SET status = 'held', held_by = $1, hold_expires_at = now() + interval '10 minutes'
WHERE event_id = $2 AND seat_id = $3
  AND (status = 'available' OR (status = 'held' AND hold_expires_at < now()));
-- 1 row → held for this buyer; 0 rows → someone else has it` },
      { type: 'ul', items: [
        'On successful payment, change the seat to booked only if this buyer still holds it.',
        'Expired holds release automatically through the condition above; a background job can also clean them up.',
        'An in-memory store with expiring keys (for example Redis) can hold seats for speed, with the database as the final record of bookings.'
      ] },

      { type: 'h2', text: '3. Deep dive: surviving the on-sale spike' },
      { type: 'p', text: 'Letting everyone hit the booking service at once overloads it and creates a chaotic experience. A **virtual waiting room** admits users in controlled batches.' },
      {"type": "diagram", "title": "Ticket booking: protecting the on-sale", "caption": "Browsing is served from the CDN. Buyers pass through a virtual waiting room that admits them in batches with signed tokens; seat holds and bookings are strongly consistent.", "cols": 4, "rows": 3, "nodes": [{"id": "fans", "label": "Fans", "kind": "client", "col": 0, "row": 1}, {"id": "cdn", "label": "CDN", "kind": "edge", "col": 1, "row": 0, "sub": "event pages, seat map"}, {"id": "wait", "label": "Waiting room", "kind": "edge", "col": 1, "row": 1, "sub": "admits in batches"}, {"id": "book", "label": "Booking service", "kind": "service", "col": 2, "row": 1, "sub": "requires admission token"}, {"id": "holds", "label": "Seat holds", "kind": "cache", "col": 3, "row": 0, "sub": "expiring"}, {"id": "seats", "label": "Seats & bookings", "kind": "db", "col": 3, "row": 1}, {"id": "psp", "label": "Payment provider", "kind": "external", "col": 3, "row": 2}], "edges": [{"from": "fans", "to": "cdn", "label": "browse"}, {"from": "fans", "to": "wait", "label": "join queue"}, {"from": "wait", "to": "book", "label": "admitted"}, {"from": "book", "to": "holds", "label": "hold seat"}, {"from": "book", "to": "seats", "label": "confirm"}, {"from": "book", "to": "psp", "label": "pay"}]},
      { type: 'ul', items: [
        'Users get a signed token when admitted; the booking API rejects requests without one.',
        'Serve the event page and static seat map from a CDN; only seat availability is dynamic.',
        'Rate-limit per user and detect bots to keep the queue fair.'
      ] },

      { type: 'h2', text: '4. Showing seat availability' },
      { type: 'p', text: 'The seat map shown while browsing can be a few seconds stale, refreshed by polling or pushed over a live connection. That is acceptable because the hold operation itself is strongly consistent: if a seat was just taken, the hold fails and the user picks another.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Events, seats and bookings tables; preventing double booking.'],
        ['Mid', 'Expiring holds, atomic updates, caching the catalogue.'],
        ['Senior', 'Waiting room, admission tokens, fairness and bots, separating strongly and eventually consistent parts.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Checking availability and then booking in two separate steps.',
        'Holds that never expire.',
        'No plan for the thundering herd at on-sale time.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'Why not lock the whole event while someone books?', answer: ['It would serialise every buyer for that event and make popular on-sales impossibly slow. Locking per seat (via conditional updates) lets thousands of people book different seats in parallel.'] },
      { type: 'qa', question: 'How do you handle general admission (no specific seats)?', answer: ['Keep a counter of remaining tickets and decrement it atomically only if it is above zero. Holds reserve a quantity with an expiry, returning it to the pool if not paid.'] },

      { type: 'exercise', title: 'Design the waiting room', task: 'An event with 20,000 seats expects 500,000 users at 10:00. Describe how users are admitted and how the booking service is protected.', solution: `- From 9:45, users join the waiting room; at 10:00 their order is
  randomised (fairer than "who clicked first")
- Admit users in batches sized to booking capacity (e.g. 2,000/min),
  issuing short-lived signed admission tokens
- Booking APIs require a valid token; tokens expire if unused
- Show queue position and estimated wait; close the queue when
  seats sell out
- Event page and seat map assets come from the CDN`, language: 'text' },

      { type: 'summary', items: [
        'Seat ownership needs strong consistency; browsing does not.',
        'Atomic conditional updates and expiring holds prevent double booking.',
        'A virtual waiting room turns a stampede into a steady flow.',
        'Cache everything that is not seat ownership.'
      ] }
    ]
  },
  {
    slug: 'design-web-crawler',
    title: 'Case Study: Design a Web Crawler',
    summary: 'Crawling billions of pages politely: the URL frontier, deduplication, politeness and failure handling.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a crawler that downloads web pages for a search engine.” It is a pipeline problem: keep a huge list of URLs to visit, fetch them fast without hammering any single website, skip duplicates, and keep going when parts fail.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Start from seed URLs; fetch pages and extract new links
  2. Store page content for indexing
  3. Revisit pages periodically to keep them fresh

Non-functional
  - Billions of pages; thousands of fetches per second
  - Politeness: respect robots.txt and limit requests per website
  - Robust to slow sites, traps and failures` },

      { type: 'h2', text: '2. The pipeline' },
      {"type": "diagram", "title": "Web crawler: the pipeline", "caption": "Fetchers take URLs from per-host queues so each site is crawled politely. Parsed pages are stored, and new links are normalised and checked against the seen set before entering the frontier.", "cols": 4, "rows": 3, "nodes": [{"id": "seeds", "label": "Seed URLs", "kind": "storage", "col": 0, "row": 0}, {"id": "frontier", "label": "URL frontier", "kind": "queue", "col": 1, "row": 0, "sub": "per-host queues"}, {"id": "fetch", "label": "Fetchers", "kind": "worker", "col": 2, "row": 0, "sub": "robots.txt, delays"}, {"id": "web", "label": "Websites", "kind": "external", "col": 3, "row": 0}, {"id": "parse", "label": "Parser", "kind": "worker", "col": 2, "row": 1}, {"id": "seen", "label": "Seen URLs", "kind": "cache", "col": 1, "row": 1, "sub": "Bloom filter + store"}, {"id": "content", "label": "Page content", "kind": "storage", "col": 2, "row": 2}], "edges": [{"from": "seeds", "to": "frontier"}, {"from": "frontier", "to": "fetch", "label": "next URL per host"}, {"from": "fetch", "to": "web", "label": "GET"}, {"from": "fetch", "to": "parse", "label": "HTML"}, {"from": "parse", "to": "content", "label": "store"}, {"from": "parse", "to": "seen", "label": "new links"}, {"from": "seen", "to": "frontier", "label": "unseen only"}]},

      { type: 'h2', text: '3. Deep dive: the URL frontier and politeness' },
      { type: 'ul', items: [
        'Keep a separate queue per host, and allow only one fetch at a time per host with a delay between requests.',
        'Fetchers pick hosts whose delay has passed, so many sites are crawled in parallel while each individual site sees gentle traffic.',
        'Prioritise important or frequently changing pages for earlier and more frequent visits.',
        'Cache robots.txt per host and obey it.'
      ] },

      { type: 'h2', text: '4. Deep dive: avoiding duplicates' },
      { type: 'p', text: 'The same page appears under many URLs (tracking parameters, trailing slashes, http vs https). Normalise URLs before checking whether you have seen them.' },
      { type: 'code', variants: [
        { language: 'java', code: `import java.net.URI;
import java.util.Arrays;
import java.util.stream.Collectors;

public class Normalise {
    static String normalise(String raw) throws Exception {
        URI uri = new URI(raw);
        String query = uri.getRawQuery() == null ? "" : Arrays.stream(uri.getRawQuery().split("&"))
            .filter(param -> !param.startsWith("utm_"))      // drop tracking params
            .sorted()
            .collect(Collectors.joining("&"));
        String path = uri.getPath();
        if (path.length() > 1 && path.endsWith("/")) path = path.substring(0, path.length() - 1);
        return new URI(uri.getScheme(), uri.getHost().toLowerCase(), path,
                       query.isEmpty() ? null : query, null).toString();   // fragment dropped
    }

    public static void main(String[] args) throws Exception {
        System.out.println(normalise("https://Example.com/blog/?b=2&utm_source=x&a=1#top"));
    }
}`, output: `https://example.com/blog?a=1&b=2` },
        { language: 'python', code: `from urllib.parse import urlsplit, urlunsplit, parse_qsl, urlencode

def normalise(raw):
    parts = urlsplit(raw)
    query = sorted((k, v) for k, v in parse_qsl(parts.query) if not k.startswith("utm_"))
    path = parts.path.rstrip("/") if len(parts.path) > 1 else parts.path
    return urlunsplit((parts.scheme, parts.netloc.lower(), path, urlencode(query), ""))  # fragment dropped

print(normalise("https://Example.com/blog/?b=2&utm_source=x&a=1#top"))`, output: `https://example.com/blog?a=1&b=2` },
        { language: 'javascript', code: `function normalise(raw) {
  const url = new URL(raw);
  url.hash = "";
  url.hostname = url.hostname.toLowerCase();
  for (const p of [...url.searchParams.keys()]) {
    if (p.startsWith("utm_")) url.searchParams.delete(p);   // drop tracking params
  }
  url.searchParams.sort();
  if (url.pathname.length > 1 && url.pathname.endsWith("/")) url.pathname = url.pathname.slice(0, -1);
  return url.toString();
}

console.log(normalise("https://Example.com/blog/?b=2&utm_source=x&a=1#top"));`, output: `https://example.com/blog?a=1&b=2` }
      ] },
      { type: 'ul', items: [
        'Store normalised URLs (or their hashes) in a large seen-set; a Bloom filter answers “definitely not seen” cheaply in memory.',
        'Detect duplicate content by hashing page content, since different URLs can serve identical pages.'
      ] },

      { type: 'h2', text: '5. Robustness' },
      { type: 'ul', items: [
        'Timeouts and size limits per fetch, so one slow or huge page cannot block a fetcher.',
        'Limit crawl depth per site to avoid infinite “calendar” traps that generate endless URLs.',
        'Persist the frontier so a crash does not lose progress.',
        'Scale fetchers horizontally; partition the frontier by host so each host is handled by one place.'
      ] },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'A queue of URLs, fetch, parse, store, extract links.'],
        ['Mid', 'Deduplication, robots.txt, per-host rate limiting.'],
        ['Senior', 'Frontier design with priorities and per-host queues, partitioning, traps, recrawl scheduling, content deduplication.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Ignoring politeness and overloading websites.',
        'No URL normalisation, so the same page is crawled endlessly.',
        'Keeping the frontier only in memory.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you decide how often to recrawl a page?', answer: ['Track how often each page changed in past visits and its importance. Pages that change often or matter most are revisited frequently; static pages less often.'] },
      { type: 'qa', question: 'Why use a Bloom filter for seen URLs?', answer: ['It answers “definitely not seen” or “probably seen” using very little memory, which matters with billions of URLs. Occasional false positives mean skipping a few new URLs, which is usually acceptable.'] },

      { type: 'exercise', title: 'Handle a slow website', task: 'One large website responds slowly and has millions of pages. How do you crawl it without blocking the rest of the crawl or hurting the site?', solution: `- Its URLs sit in their own per-host queue; only one fetch runs at a
  time for that host, with a delay that respects its response times
- Fetch timeouts free up the fetcher if a request hangs
- Other hosts continue in parallel, so total throughput is unaffected
- Increase the delay automatically if the site returns errors or 429s
- Prioritise its most important pages first`, language: 'text' },

      { type: 'summary', items: [
        'A crawler is a pipeline: frontier → fetch → parse → store → new links.',
        'Per-host queues and delays keep crawling polite.',
        'Normalise URLs and hash content to avoid duplicates.',
        'Timeouts, depth limits and a persistent frontier keep it robust.'
      ] }
    ]
  },
  {
    slug: 'design-key-value-store',
    title: 'Case Study: Design a Distributed Key-Value Store',
    summary: 'Partitioning with consistent hashing, replication, quorums, failure detection and conflict resolution.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a distributed key-value store with get and put.” This question puts the fundamentals together: partitioning, replication, consistency trade-offs and handling node failures.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `API: get(key) → value, put(key, value)

Non-functional
  - Store far more data than one machine can hold
  - Highly available: keep working when nodes fail
  - Tunable consistency per request
  - Low latency for reads and writes` },

      { type: 'h2', text: '2. Partitioning' },
      { type: 'p', text: 'Spread keys across nodes with consistent hashing: nodes and keys are placed on a hash ring, and each key belongs to the next node clockwise. Many virtual positions per node keep the load even, and adding a node moves only a small share of keys.' },

      { type: 'h2', text: '3. Replication' },
      { type: 'p', text: 'Store each key on N nodes: its owner and the next N − 1 distinct nodes on the ring, ideally in different availability zones. Losing one node then never loses data.' },

      { type: 'h2', text: '4. Quorums and tunable consistency' },
      { type: 'p', text: 'A write waits for W replicas to confirm; a read asks R replicas. If R + W > N, every read overlaps at least one replica that has the latest write.' },
      {"type": "diagram", "title": "Key-value store: a quorum write (N = 3, W = 2)", "caption": "The coordinator sends the write to all three replicas and replies to the client after two acknowledge. Replicas exchange state through gossip and repair any differences in the background.", "cols": 3, "rows": 3, "nodes": [{"id": "client", "label": "Client", "kind": "client", "col": 0, "row": 1}, {"id": "coord", "label": "Coordinator", "kind": "service", "col": 1, "row": 1, "sub": "any node"}, {"id": "a", "label": "Node A", "kind": "db", "col": 2, "row": 0, "sub": "replica 1"}, {"id": "b", "label": "Node B", "kind": "db", "col": 2, "row": 1, "sub": "replica 2"}, {"id": "c", "label": "Node C", "kind": "db", "col": 2, "row": 2, "sub": "replica 3"}], "edges": [{"from": "client", "to": "coord", "label": "put(key, value)"}, {"from": "coord", "to": "a", "label": "ack 1"}, {"from": "coord", "to": "b", "label": "ack 2"}, {"from": "coord", "to": "c", "label": "async", "dashed": true}, {"from": "a", "to": "b", "label": "gossip", "dashed": true, "both": true}]},
      { type: 'code', variants: [
        { language: 'java', code: `public class Quorum {
    static String describe(int n, int w, int r) {
        String overlap = r + w > n ? "reads see the latest write" : "reads may be stale";
        return "N=" + n + " W=" + w + " R=" + r + ": " + overlap;
    }

    public static void main(String[] args) {
        System.out.println(describe(3, 2, 2));
        System.out.println(describe(3, 1, 1));
        System.out.println(describe(3, 3, 1));
    }
}`, output: `N=3 W=2 R=2: reads see the latest write
N=3 W=1 R=1: reads may be stale
N=3 W=3 R=1: reads see the latest write` },
        { language: 'python', code: `def describe(n, w, r):
    overlap = "reads see the latest write" if r + w > n else "reads may be stale"
    return f"N={n} W={w} R={r}: {overlap}"

print(describe(3, 2, 2))
print(describe(3, 1, 1))
print(describe(3, 3, 1))`, output: `N=3 W=2 R=2: reads see the latest write
N=3 W=1 R=1: reads may be stale
N=3 W=3 R=1: reads see the latest write` },
        { language: 'javascript', code: `function describe(N, W, R) {
  const overlap = R + W > N ? "reads see the latest write" : "reads may be stale";
  return \`N=\${N} W=\${W} R=\${R}: \${overlap}\`;
}

console.log(describe(3, 2, 2));
console.log(describe(3, 1, 1));
console.log(describe(3, 3, 1));`, output: `N=3 W=2 R=2: reads see the latest write
N=3 W=1 R=1: reads may be stale
N=3 W=3 R=1: reads see the latest write` }
      ] },
      { type: 'table', headers: ['Setting', 'Behaviour'], rows: [
        ['W=2, R=2 (N=3)', 'Balanced consistency and availability'],
        ['W=1, R=1', 'Fastest and most available; reads can be stale'],
        ['W=3, R=1', 'Fast reads, but writes fail if any replica is down']
      ] },

      { type: 'h2', text: '5. Handling failures' },
      { type: 'ul', items: [
        '**Failure detection**: nodes exchange heartbeats (often via gossip) and mark peers as suspected down after missed beats.',
        '**Hinted handoff**: if a replica is temporarily down, another node accepts its writes and hands them over when it returns.',
        '**Anti-entropy repair**: replicas periodically compare data (efficiently, with hash trees) and fix differences.',
        '**Read repair**: when a read finds replicas disagreeing, the newest value is written back to stale replicas.'
      ] },

      { type: 'h2', text: '6. Conflicts' },
      { type: 'p', text: 'With high availability, two clients can update the same key on different replicas during a partition. Options: last-write-wins using timestamps (simple, can silently lose an update), keeping both versions and letting the application merge them, or data types designed to merge automatically.' },

      { type: 'h2', text: '7. On each node' },
      { type: 'p', text: 'Writes are appended to a commit log for durability and kept in an in-memory table, which is flushed to sorted files on disk when full. Background compaction merges those files. This log-structured design makes writes fast because they are sequential.' },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Partitioning keys across machines and replicating them.'],
        ['Mid', 'Consistent hashing, replication factor, quorums.'],
        ['Senior', 'Failure detection, hinted handoff, repair, conflict resolution, storage engine trade-offs.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Modulo hashing that moves almost every key when nodes change.',
        'Replicas in the same rack or zone.',
        'Claiming strong consistency and full availability during partitions at once.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How does a client know which node holds a key?', answer: ['Either the client library knows the ring layout and contacts the right node directly, or any node accepts the request and forwards it to the owners (acting as a coordinator).'] },
      { type: 'qa', question: 'What happens when you add a node?', answer: ['It takes over a slice of the ring from its neighbours. Those keys are streamed to it in the background, after which it starts serving them. With consistent hashing only a small fraction of keys move.'] },

      { type: 'exercise', title: 'Pick quorum settings', task: 'With N = 3, choose W and R for: (a) a shopping cart that must always accept additions, (b) user account balances, (c) a cache of product views.', solution: `(a) W=1, R=1 (or R=2) with merge-on-read: always writable, merge
    cart versions so no addition is lost
(b) W=2, R=2 (R+W>N), or a strongly consistent store: balances
    must not be stale
(c) W=1, R=1: speed matters, staleness is harmless`, language: 'text' },

      { type: 'summary', items: [
        'Consistent hashing partitions keys; replicas live on the next nodes.',
        'Quorums (R + W > N) tune consistency against latency and availability.',
        'Gossip, hinted handoff and repair keep data available and converging.',
        'Decide how conflicts are resolved before they happen.'
      ] }
    ]
  },
  {
    slug: 'design-live-leaderboard',
    title: 'Case Study: Design a Live Leaderboard',
    summary: 'Ranking millions of players in real time with sorted sets, sharding scores, and showing “your rank”.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'The prompt' },
      { type: 'p', text: '“Design a real-time leaderboard for a game (or a coding contest).” Players’ scores change constantly, and everyone wants to see the top players and their own rank instantly.' },

      { type: 'h2', text: '1. Requirements' },
      { type: 'code', language: 'text', code: `Functional
  1. Update a player's score when they finish a match or solve a problem
  2. Show the top 100 players
  3. Show any player's rank and the players around them

Non-functional
  - Updates visible within a second or two
  - ~10M players per leaderboard; ~10,000 score updates per second at peak` },

      { type: 'h2', text: '2. Why a normal database query struggles' },
      { type: 'p', text: '`SELECT COUNT(*) FROM scores WHERE score > :myScore` gives a player’s rank, but on 10 million rows it is slow, and running it for many players constantly is expensive. You need a structure that keeps players sorted as scores change.' },

      { type: 'h2', text: '3. Sorted sets' },
      { type: 'p', text: 'A sorted set keeps members ordered by score and can update a score, find a member’s rank, and return a range of ranks in about O(log n). Redis sorted sets are the classic tool for this.' },
      { type: 'code', language: 'text', code: `ZINCRBY contest:42 100 "player:7"            add 100 points
ZREVRANGE contest:42 0 99 WITHSCORES         top 100
ZREVRANK contest:42 "player:7"               this player's rank (0-based)
ZREVRANGE contest:42 (rank-5) (rank+5)       players around them` },
      { type: 'code', variants: [
        { language: 'java', code: `import java.util.*;
import java.util.stream.*;

public class Leaderboard {
    public static void main(String[] args) {
        Map<String, Integer> scores = new HashMap<>(Map.of("asha", 950, "ravi", 1200, "meera", 1200, "kiran", 400));
        scores.merge("kiran", 900, Integer::sum);                       // kiran finishes a match

        List<Map.Entry<String, Integer>> ranked = scores.entrySet().stream()
            .sorted(Map.Entry.<String, Integer>comparingByValue().reversed()
                    .thenComparing(Map.Entry.<String, Integer>comparingByKey()))        // tie-break by name
            .collect(Collectors.toList());

        System.out.println(IntStream.range(0, ranked.size())
            .mapToObj(i -> (i + 1) + ". " + ranked.get(i).getKey() + " " + ranked.get(i).getValue())
            .collect(Collectors.joining(" | ")));
    }
}`, output: `1. kiran 1300 | 2. meera 1200 | 3. ravi 1200 | 4. asha 950` },
        { language: 'python', code: `# The same idea in plain Python, to show what the operations return
scores = {"asha": 950, "ravi": 1200, "meera": 1200, "kiran": 400}
scores["kiran"] += 900                                              # kiran finishes a match

ranked = sorted(scores.items(), key=lambda item: (-item[1], item[0]))   # tie-break by name
print(" | ".join(f"{i}. {player} {score}" for i, (player, score) in enumerate(ranked, 1)))`, output: `1. kiran 1300 | 2. meera 1200 | 3. ravi 1200 | 4. asha 950` },
        { language: 'javascript', code: `// The same idea in plain JavaScript, to show what the operations return
const scores = new Map([["asha", 950], ["ravi", 1200], ["meera", 1200], ["kiran", 400]]);

const ranked = () => [...scores.entries()]
  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))   // tie-break by name
  .map(([player, score], i) => ({ rank: i + 1, player, score }));

scores.set("kiran", scores.get("kiran") + 900);               // kiran finishes a match
console.log(ranked().map((r) => \`\${r.rank}. \${r.player} \${r.score}\`).join(" | "));`, output: `1. kiran 1300 | 2. meera 1200 | 3. ravi 1200 | 4. asha 950` }
      ] },
      { type: 'callout', variant: 'note', title: 'Ties need a rule', text: 'meera and ravi both have 1,200 points. Here they are ordered alphabetically, which is arbitrary. Real leaderboards usually reward whoever reached the score first; see the interview question on tie-breaking below.' },

      { type: 'h2', text: '4. Durability and the source of truth' },
      { type: 'ul', items: [
        'Record each score change durably (database or event stream), then update the sorted set.',
        'If the in-memory leaderboard is lost, rebuild it from the durable records.',
        'Make updates idempotent by match or submission ID so retries do not double-count points.'
      ] },

      {"type": "diagram", "title": "Live leaderboard: data flow", "caption": "Score changes are recorded durably and applied to Redis sorted sets, which answer top-N and rank queries quickly. The sorted sets can always be rebuilt from the scores database.", "cols": 4, "rows": 3, "nodes": [{"id": "game", "label": "Game / judge servers", "kind": "service", "col": 0, "row": 1}, {"id": "events", "label": "Score events", "kind": "queue", "col": 1, "row": 1, "sub": "idempotent by match id"}, {"id": "upd", "label": "Score updater", "kind": "worker", "col": 2, "row": 1}, {"id": "scores", "label": "Scores DB", "kind": "db", "col": 2, "row": 2, "sub": "source of truth"}, {"id": "zset", "label": "Sorted sets", "kind": "cache", "col": 3, "row": 1, "sub": "per board & period"}, {"id": "players", "label": "Players", "kind": "client", "col": 1, "row": 0}, {"id": "api", "label": "Leaderboard API", "kind": "service", "col": 2, "row": 0, "sub": "top 100 cached"}], "edges": [{"from": "game", "to": "events", "label": "match finished", "dashed": true}, {"from": "events", "to": "upd", "dashed": true}, {"from": "upd", "to": "scores", "label": "record"}, {"from": "upd", "to": "zset", "label": "ZINCRBY"}, {"from": "players", "to": "api", "label": "top / my rank"}, {"from": "api", "to": "zset", "label": "ZREVRANGE / ZREVRANK"}]},
      { type: 'h2', text: '5. Scaling' },
      { type: 'ul', items: [
        'One sorted set of 10 million members fits in memory on one node; replicate it for reads.',
        'For far larger boards, shard by score range (each shard knows how many players rank above it) or keep exact ranks only for the top players and approximate ranks (percentiles) for everyone else.',
        'Cache the top 100 for a second or two; it is requested constantly and changes slowly relative to request volume.',
        'Push leaderboard changes to viewers over a live connection instead of having everyone poll.'
      ] },

      { type: 'h2', text: 'What each level should cover' },
      { type: 'table', headers: ['Level', 'Expected'], rows: [
        ['Junior', 'Storing scores and computing the top N.'],
        ['Mid', 'Sorted sets for rank and range queries, caching the top list.'],
        ['Senior', 'Durability and rebuilds, idempotent updates, very large boards, ties, time-windowed leaderboards (daily, weekly).']
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'How do you support daily and weekly leaderboards?', answer: ['Keep a separate sorted set per period (for example `contest:42:2026-10-29`) with an expiry after the period ends, and update all relevant sets on each score change.'] },
      { type: 'qa', question: 'How do you break ties fairly?', answer: ['Decide the rule (earlier achievement wins, for example) and encode it in the score, such as combining the points with a time component so that equal points are ordered by who reached them first.'] },

      { type: 'exercise', title: 'Show the players around me', task: 'Using a sorted set, describe the exact steps to show a player their rank plus the 5 players above and below them, and explain the cost of each step for 10 million players.', solution: `1. rank = ZREVRANK board "player:7"            → O(log n)
   (null if the player has no score yet: show "not ranked")
2. start = max(0, rank - 5), end = rank + 5
3. ZREVRANGE board start end WITHSCORES        → O(log n + 11)
4. Fetch display names for those 11 player ids in one batched lookup
   (cached), and return ranks as start + 1, start + 2, ...
Total: a couple of fast operations, independent of board size`, language: 'text' },

      { type: 'summary', items: [
        'Sorted sets give rank, range and score updates in about O(log n).',
        'Keep a durable source of truth and rebuild the in-memory board from it.',
        'Make score updates idempotent.',
        'Cache and push the top list; approximate ranks for very large boards.'
      ] }
    ]
  }
];
